import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { WizardStep } from "@sscs/types";
import { Dialog, Toast, Wizard } from "@sscs/ui";
import { buildRlPurl, formatCount } from "@sscs/utils";
import { PACKAGE_ROUTE, PROJECT_ROUTE } from "Constants";
import { useFileUploadChecks, useProjectsRoute } from "Hooks";
import { getImportableVersions } from "Modules/importableVersions";
import {
  useGetExternalCatalogPackageQuery,
  useGetProjectsSearchQuery,
} from "Services";
import { PackageFormData, ProjectFormData } from "Types";
import {
  apiErrorToast,
  getCleanValue,
  getPackageFormSchema,
  getProjectFormSchema,
} from "Utils";
import useCatalogParams from "../../hooks/useCatalogParams";
import { useImportExternalPackageMutation } from "../../services/softwareCatalog.api";
import { ExternalPackageSummary } from "../../types";
import { getUniqueName } from "../../utils";
import styles from "./importPackage.module.scss";
import Overview from "./Overview";
import Package from "./Package";
import Project from "./Project";
import Versions, { VersionsForm, getVersionsSchema } from "./Versions";

type ImportPackageProps = {
  catalogName: string;
  externalPackage: ExternalPackageSummary;
  onClose: () => void;
};

const ImportPackageContent = ({
  catalogName,
  externalPackage,
  onClose,
}: ImportPackageProps): React.JSX.Element => {
  const { groupId, catalogId } = useCatalogParams();
  const [activeStep, setActiveStep] = useState(0);
  const [project, setProject] = useState<ProjectFormData | null>(null);
  const [packageData, setPackageData] = useState<PackageFormData | null>(null);
  const [versions, setVersions] = useState<VersionsForm | null>(null);
  const { data: softwareDetails } = useGetExternalCatalogPackageQuery(
    groupId && catalogId
      ? {
          groupId,
          catalog: catalogId,
          externalPackageId: externalPackage.id,
        }
      : skipToken,
  );
  const { data: projectsSearch } = useGetProjectsSearchQuery(
    groupId
      ? {
          groupId,
          searchName: "",
        }
      : skipToken,
  );
  const [importPackage, { isLoading: isImporting }] =
    useImportExternalPackageMutation();
  const projectsRoute = useProjectsRoute();
  const { remainingCapacity, maxVersions } = useFileUploadChecks(
    "VERSION",
    null,
  );

  const selectedArtifact = softwareDetails?.versions
    .flatMap((version) => version.artifacts)
    .find(
      (artifact) => artifact.identifier.uuid === externalPackage.artifact_id,
    );
  const importableVersions = selectedArtifact
    ? getImportableVersions(softwareDetails?.versions ?? [], {
        platform: selectedArtifact.platform,
        architecture: selectedArtifact.architecture,
        extension: selectedArtifact.extension,
      })
    : [];
  const defaultVersions = importableVersions
    .slice(0, maxVersions)
    .map((item) => item.version);
  const versionsFormValues: VersionsForm = versions ?? {
    versions: defaultVersions,
    notify_on_completion: false,
    auto_sync: false,
  };
  const autoApprovalEnabled = softwareDetails?.auto_approval_enabled ?? false;
  const projects = projectsSearch?.projects ?? [];
  const projectNames = projects.map((item) => item.name);
  const selectedProjectPackageNames =
    projects
      .find((item) => item.name === project?.project_name)
      ?.packages.map((item) => item.name) ?? [];

  const handleNext = (): void => {
    setActiveStep((previousState) => previousState + 1);
  };

  const handlePrevious = (): void => {
    setActiveStep((previousState) => previousState - 1);
  };

  const handleStepChange = (step: number): void => {
    setActiveStep(step);
  };

  const handleImport = async (): Promise<void> => {
    if (!catalogId || !groupId || !project || !packageData || !versions) {
      return;
    }

    try {
      const response = await importPackage({
        catalog: catalogId,
        group_id: groupId,
        artifact_id: externalPackage.artifact_id,
        ...project,
        ...packageData,
        ...versions,
      }).unwrap();

      Toast.show({
        title: (
          <>
            Importing {formatCount(versions.versions.length, "version")} to{" "}
            <strong>
              {buildRlPurl(project.project_name, packageData.package_name)}
            </strong>
          </>
        ),
        type: "success",
        actions: [
          {
            label: "View package",
            to: `${projectsRoute}/${PROJECT_ROUTE}/${response.project_id}/${PACKAGE_ROUTE}/${response.package_id}`,
          },
        ],
      });
      onClose();
    } catch (error) {
      apiErrorToast(error, "Could not import package");
    }
  };

  return (
    <Wizard
      title={
        <>
          Import <em>{externalPackage.name}</em> from {catalogName} catalog
        </>
      }
      subtitle={`${catalogName} catalog`}
      activeStep={activeStep}
      onStepChange={handleStepChange}
      dialogContentClasses={styles["dialog-content"]}
      steps={
        [
          {
            label: "Project",
            onNext: (data: ProjectFormData) => {
              setProject({
                ...data,
                project_name: getCleanValue(data.project_name),
              });
              handleNext();
            },
            onPrevious: handlePrevious,
            formValues: project ?? {
              project_name: selectedArtifact?.path?.project ?? "",
              project_description: "",
            },
            schema: getProjectFormSchema(),
            component: (
              <Project catalogName={catalogName} projects={projectNames} />
            ),
          },
          {
            label: "Package",
            onNext: (data: PackageFormData) => {
              setPackageData({
                ...data,
                package_name: getCleanValue(data.package_name),
              });
              handleNext();
            },
            onPrevious: handlePrevious,
            formValues: packageData ?? {
              package_name: getUniqueName(
                selectedArtifact?.path?.package ?? "",
                selectedProjectPackageNames,
              ),
              package_description: externalPackage.summary,
            },
            schema: getPackageFormSchema(selectedProjectPackageNames),
            component: <Package />,
          },
          {
            label: "Versions",
            onNext: (data: VersionsForm) => {
              setVersions(data);
              handleNext();
            },
            onPrevious: handlePrevious,
            formValues: versionsFormValues,
            schema: getVersionsSchema(
              importableVersions,
              remainingCapacity,
              maxVersions,
            ),
            component: (
              <Versions
                catalogName={catalogName}
                versions={importableVersions}
                remainingCapacity={remainingCapacity}
                maxVersions={maxVersions}
                autoApprovalEnabled={autoApprovalEnabled}
              />
            ),
          },
          {
            label: "Overview",
            nextStep: { label: "Import", isLoading: isImporting },
            onNext: handleImport,
            onPrevious: handlePrevious,
            component: (
              <Overview
                catalogName={catalogName}
                externalPackage={externalPackage}
                projectName={project?.project_name ?? ""}
                packageName={packageData?.package_name ?? ""}
                selectedVersions={importableVersions.filter((item) =>
                  versionsFormValues.versions.includes(item.version),
                )}
                totalVersions={importableVersions.length}
                autoSync={versionsFormValues.auto_sync}
                notifyOnCompletion={versionsFormValues.notify_on_completion}
                remainingCapacity={remainingCapacity}
                autoApprovalEnabled={autoApprovalEnabled}
              />
            ),
          },
        ] satisfies [
          WizardStep<ProjectFormData>,
          WizardStep<PackageFormData>,
          WizardStep<VersionsForm>,
          WizardStep,
        ]
      }
    />
  );
};

const ImportPackage = (props: ImportPackageProps): React.JSX.Element => (
  <Dialog.Frame isOpen onClose={props.onClose} size="md">
    <ImportPackageContent {...props} />
  </Dialog.Frame>
);

export default ImportPackage;
